import React, { useState, useEffect } from 'react';
import { 
  Wallet, 
  TrendingUp, 
  PieChart, 
  Sparkles, 
  Shield, 
  Zap,
  Target,
  BarChart3,
  Bell,
  Lightbulb,
  ArrowRight,
  CheckCircle2,
  Lock,
  Eye,
  Receipt,
  X,
  ChevronRight,
  Sun,
  Moon
} from 'lucide-react';
import Login from '../Auth/Login';
import Signup from '../Auth/Signup';
import { useTheme } from '../../Context/ThemeContext';
import styles from './LandingPage.module.scss';

interface LandingPageProps {
  onAuthenticated: () => void;
}

const LandingPage: React.FC<LandingPageProps> = ({ onAuthenticated }) => {
  const [showAuth, setShowAuth] = useState(false);
  const [authMode, setAuthMode] = useState<'login' | 'signup'>('signup');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { toggleTheme, isDark } = useTheme();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleGetStarted = () => {
    setAuthMode('signup');
    setShowAuth(true);
  };

  const handleLogin = () => {
    setAuthMode('login');
    setShowAuth(true);
  };

  const handleCloseAuth = () => {
    setShowAuth(false);
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  return (
    <div className={styles.landingPage}>
      {/* Sticky Navigation */}
      <nav className={`${styles.navbar} ${scrolled ? styles.navScrolled : ''}`}>
        <div className={styles.navContainer}>
          <div className={styles.navLogo}>
            <span className={styles.logoIcon}>Z</span>
            <span className={styles.logoText}>ZEBED</span>
          </div>
          
          {/* Desktop Menu */}
          <div className={styles.navLinks}>
            <button onClick={() => scrollToSection('features')} className={styles.navLink}>Features</button>
            <button onClick={() => scrollToSection('how-it-works')} className={styles.navLink}>How It Works</button>
            <button onClick={() => scrollToSection('dashboard-preview')} className={styles.navLink}>Dashboard</button>
            <button onClick={() => scrollToSection('security')} className={styles.navLink}>Security</button>
          </div>

          <div className={styles.navActions}>
            <button onClick={toggleTheme} className={styles.navThemeBtn} title={isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'}>
              {isDark ? <Sun size={18} /> : <Moon size={18} />}
            </button>
            <button onClick={handleLogin} className={styles.navLoginBtn}>Sign In</button>
            <button onClick={handleGetStarted} className={styles.navCtaBtn}>
              Get Started
              <ArrowRight size={18} />
            </button>
          </div>

          {/* Mobile Hamburger */}
          <button 
            className={`${styles.hamburger} ${mobileMenuOpen ? styles.hamburgerOpen : ''}`}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>

        {/* Mobile Menu */}
        <div className={`${styles.mobileMenu} ${mobileMenuOpen ? styles.mobileMenuOpen : ''}`}>
          <button onClick={() => scrollToSection('features')} className={styles.mobileNavLink}>Features</button>
          <button onClick={() => scrollToSection('how-it-works')} className={styles.mobileNavLink}>How It Works</button>
          <button onClick={() => scrollToSection('dashboard-preview')} className={styles.mobileNavLink}>Dashboard</button>
          <button onClick={() => scrollToSection('security')} className={styles.mobileNavLink}>Security</button>
          <div className={styles.mobileNavDivider}></div>
          <button onClick={handleLogin} className={styles.mobileNavLink}>Sign In</button>
          <button onClick={handleGetStarted} className={styles.mobileNavCta}>
            Get Started
            <ArrowRight size={18} />
          </button>
        </div>
      </nav>

      {/* Auth Modal */}
      {showAuth && (
        <div className={styles.modalOverlay} onClick={handleCloseAuth}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <button className={styles.modalClose} onClick={handleCloseAuth}>
              <X size={24} />
            </button>
            {authMode === 'login' ? (
              <Login onSwitchToSignup={() => setAuthMode('signup')} />
            ) : (
              <Signup onSwitchToLogin={() => setAuthMode('login')} />
            )}
          </div>
        </div>
      )}

      {/* Hero Section */}
      <header className={styles.hero}>
        <div className={styles.heroGlow}></div>
        <div className={styles.heroContent}>
          <div className={styles.heroBadge}>
            <Sparkles size={16} />
            <span>Smart Finance for Smart People</span>
          </div>
          
          <h1 className={styles.heroTitle}>
            Master Your Money,
            <br />
            <span className={styles.highlight}>Build Your Future</span>
          </h1>
          
          <p className={styles.heroSubtitle}>
            ZEBED is the all-in-one financial platform that helps you track spending, 
            manage bills, crush debt, and grow your wealth — powered by AI insights 
            and real-time bank connections.
          </p>
          
          <div className={styles.heroCTA}>
            <button className={styles.primaryBtn} onClick={handleGetStarted}>
              Start Free Today
              <ArrowRight size={20} />
            </button>
            <button className={styles.secondaryBtn} onClick={() => scrollToSection('dashboard-preview')}>
              See It In Action
              <Eye size={20} />
            </button>
          </div>
        </div>
        
        <div className={styles.heroVisual}>
          <div className={styles.dashboardPreview}>
            <div className={styles.previewHeader}>
              <div className={styles.previewDots}>
                <span></span><span></span><span></span>
              </div>
              <span className={styles.previewTitle}>ZEBED Dashboard</span>
            </div>
            <div className={styles.previewContent}>
              <div className={styles.previewCard}>
                <div className={styles.previewCardIcon}><Wallet size={20} /></div>
                <div className={styles.previewCardInfo}>
                  <span className={styles.previewLabel}>Total Balance</span>
                  <span className={styles.previewAmount}>$12,847.50</span>
                </div>
                <div className={styles.previewTrend}>+12.5%</div>
              </div>
              <div className={styles.previewCard}>
                <div className={styles.previewCardIcon}><TrendingUp size={20} /></div>
                <div className={styles.previewCardInfo}>
                  <span className={styles.previewLabel}>Monthly Savings</span>
                  <span className={styles.previewAmount}>$2,340.00</span>
                </div>
                <div className={styles.previewTrend}>+8.2%</div>
              </div>
              <div className={styles.previewCard}>
                <div className={styles.previewCardIcon}><PieChart size={20} /></div>
                <div className={styles.previewCardInfo}>
                  <span className={styles.previewLabel}>Budget Status</span>
                  <span className={styles.previewAmount}>On Track</span>
                </div>
                <div className={styles.previewBadge}>✓</div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Trusted By */}
      <section className={styles.trustedBy}>
        <p>Securely connected with Canadian banks</p>
        <div className={styles.trustedLogos}>
          <span>🏦 Desjardins</span>
          <span>🏦 BMO</span>
          <span>🏦 Scotia</span>
          <span>🏦 TD</span>
          <span>🏦 RBC</span>
          <span>+ 12,000 more</span>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className={styles.features}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionBadge}>FEATURES</span>
          <h2 className={styles.sectionTitle}>Everything You Need to Win With Money</h2>
          <p className={styles.sectionSubtitle}>
            Powerful tools designed for the modern era of personal finance
          </p>
        </div>
        
        <div className={styles.featureGrid}>
          <div className={styles.featureCard}>
            <div className={styles.featureIcon}><Wallet size={28} /></div>
            <h3>Real-Time Balance Sync</h3>
            <p>Connect all your accounts and see your complete financial picture updated instantly across every platform.</p>
          </div>
          
          <div className={styles.featureCard}>
            <div className={styles.featureIcon}><PieChart size={28} /></div>
            <h3>Smart Categorization</h3>
            <p>AI automatically categorizes every transaction so you always know exactly where your money is going.</p>
          </div>
          
          <div className={styles.featureCard}>
            <div className={styles.featureIcon}><Sparkles size={28} /></div>
            <h3>AI-Powered Insights</h3>
            <p>Get personalized recommendations and spending summaries generated by advanced AI to optimize your finances.</p>
          </div>
          
          <div className={styles.featureCard}>
            <div className={styles.featureIcon}><BarChart3 size={28} /></div>
            <h3>Advanced Analytics</h3>
            <p>Beautiful charts and reports that visualize your spending patterns, income trends, and net worth growth.</p>
          </div>
          
          <div className={styles.featureCard}>
            <div className={styles.featureIcon}><Target size={28} /></div>
            <h3>Budget Goals</h3>
            <p>Set custom budgets for any category and track your progress with real-time alerts and notifications.</p>
          </div>
          
          <div className={styles.featureCard}>
            <div className={styles.featureIcon}><Bell size={28} /></div>
            <h3>Smart Notifications</h3>
            <p>Get daily, weekly, or monthly spending reports delivered straight to your inbox with actionable insights.</p>
          </div>
        </div>
      </section>

      {/* Dashboard Preview Section */}
      <section id="dashboard-preview" className={styles.dashboardSection}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionBadge}>DASHBOARD</span>
          <h2 className={styles.sectionTitle}>Your Financial Command Center</h2>
          <p className={styles.sectionSubtitle}>
            A beautiful, intuitive dashboard that puts you in control
          </p>
        </div>

        <div className={styles.dashboardShowcase}>
          <div className={styles.showcaseNav}>
            <div className={styles.showcaseLogo}>
              <span className={styles.showcaseLogoIcon}>Z</span>
              <span>ZEBED</span>
            </div>
            <div className={styles.showcaseMenu}>
              <span className={`${styles.showcaseMenuItem} ${styles.active}`}>Overview</span>
              <span className={styles.showcaseMenuItem}>Transactions</span>
              <span className={styles.showcaseMenuItem}>Spending</span>
              <span className={styles.showcaseMenuItem}>Budget</span>
              <span className={styles.showcaseMenuItem}>Reports</span>
            </div>
          </div>
          
          <div className={styles.showcaseContent}>
            <div className={styles.showcaseGrid}>
              <div className={`${styles.showcaseWidget} ${styles.widgetLarge}`}>
                <h4>Net Worth Overview</h4>
                <div className={styles.widgetValue}>$24,567.89</div>
                <div className={styles.widgetChart}>
                  <div className={styles.chartBar} style={{height: '60%'}}></div>
                  <div className={styles.chartBar} style={{height: '45%'}}></div>
                  <div className={styles.chartBar} style={{height: '70%'}}></div>
                  <div className={styles.chartBar} style={{height: '55%'}}></div>
                  <div className={styles.chartBar} style={{height: '80%'}}></div>
                  <div className={styles.chartBar} style={{height: '65%'}}></div>
                  <div className={`${styles.chartBar} ${styles.chartBarActive}`} style={{height: '90%'}}></div>
                </div>
              </div>
              
              <div className={styles.showcaseWidget}>
                <h4>Monthly Spending</h4>
                <div className={styles.widgetValue}>$3,420</div>
                <div className={styles.widgetSubtext}>-12% vs last month</div>
              </div>
              
              <div className={styles.showcaseWidget}>
                <h4>Savings Rate</h4>
                <div className={styles.widgetValue}>24%</div>
                <div className={styles.widgetSubtext}>Above target 🎯</div>
              </div>
              
              <div className={`${styles.showcaseWidget} ${styles.widgetWide}`}>
                <h4>Recent Transactions</h4>
                <div className={styles.transactionList}>
                  <div className={styles.transaction}>
                    <span className={styles.transactionIcon}>🛒</span>
                    <span className={styles.transactionName}>Grocery Store</span>
                    <span className={styles.transactionAmount}>-$67.50</span>
                  </div>
                  <div className={styles.transaction}>
                    <span className={styles.transactionIcon}>☕</span>
                    <span className={styles.transactionName}>Coffee Shop</span>
                    <span className={styles.transactionAmount}>-$5.75</span>
                  </div>
                  <div className={styles.transaction}>
                    <span className={styles.transactionIcon}>💰</span>
                    <span className={styles.transactionName}>Payroll Deposit</span>
                    <span className={styles.transactionAmountPositive}>+$2,450.00</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className={styles.howItWorks}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionBadge}>GET STARTED</span>
          <h2 className={styles.sectionTitle}>Up and Running in Minutes</h2>
          <p className={styles.sectionSubtitle}>
            Three simple steps to financial clarity
          </p>
        </div>
        
        <div className={styles.steps}>
          <div className={styles.step}>
            <div className={styles.stepNumber}>1</div>
            <div className={styles.stepContent}>
              <h3>Create Your Account</h3>
              <p>Sign up in seconds with your email. No credit card required, ever. Your account is free forever.</p>
            </div>
          </div>
          
          <div className={styles.stepConnector}>
            <ChevronRight size={32} />
          </div>
          
          <div className={styles.step}>
            <div className={styles.stepNumber}>2</div>
            <div className={styles.stepContent}>
              <h3>Connect Your Banks</h3>
              <p>Securely link your accounts through Plaid. We never see or store your bank login credentials.</p>
            </div>
          </div>
          
          <div className={styles.stepConnector}>
            <ChevronRight size={32} />
          </div>
          
          <div className={styles.step}>
            <div className={styles.stepNumber}>3</div>
            <div className={styles.stepContent}>
              <h3>Take Control</h3>
              <p>Watch your financial life come together. Track, analyze, optimize, and grow your wealth.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Feature Highlights */}
      <section className={styles.highlights}>
        <div className={styles.highlightCard}>
          <div className={styles.highlightContent}>
            <span className={styles.highlightBadge}><Receipt size={18} /> SPENDING</span>
            <h3>Know Where Every Dollar Goes</h3>
            <p>
              Automatic transaction categorization powered by AI gives you a crystal-clear 
              view of your spending habits. Identify patterns, find savings opportunities, 
              and make smarter financial decisions.
            </p>
            <ul className={styles.highlightList}>
              <li><CheckCircle2 size={18} /> Auto-categorized transactions</li>
              <li><CheckCircle2 size={18} /> Visual spending breakdowns</li>
              <li><CheckCircle2 size={18} /> Category-specific budgets</li>
              <li><CheckCircle2 size={18} /> Merchant insights</li>
            </ul>
          </div>
          <div className={styles.highlightVisual}>
            <div className={styles.spendingChart}>
              <div className={styles.spendingItem}>
                <span className={styles.spendingCategory}>🏠 Housing</span>
                <div className={styles.spendingBar}><div style={{width: '45%'}}></div></div>
                <span className={styles.spendingPercent}>45%</span>
              </div>
              <div className={styles.spendingItem}>
                <span className={styles.spendingCategory}>🍔 Food</span>
                <div className={styles.spendingBar}><div style={{width: '20%'}}></div></div>
                <span className={styles.spendingPercent}>20%</span>
              </div>
              <div className={styles.spendingItem}>
                <span className={styles.spendingCategory}>🚗 Transport</span>
                <div className={styles.spendingBar}><div style={{width: '15%'}}></div></div>
                <span className={styles.spendingPercent}>15%</span>
              </div>
              <div className={styles.spendingItem}>
                <span className={styles.spendingCategory}>🎬 Entertainment</span>
                <div className={styles.spendingBar}><div style={{width: '10%'}}></div></div>
                <span className={styles.spendingPercent}>10%</span>
              </div>
              <div className={styles.spendingItem}>
                <span className={styles.spendingCategory}>📦 Other</span>
                <div className={styles.spendingBar}><div style={{width: '10%'}}></div></div>
                <span className={styles.spendingPercent}>10%</span>
              </div>
            </div>
          </div>
        </div>

        <div className={`${styles.highlightCard} ${styles.highlightReverse}`}>
          <div className={styles.highlightContent}>
            <span className={styles.highlightBadge}><Lightbulb size={18} /> AI INSIGHTS</span>
            <h3>Your Personal Finance AI</h3>
            <p>
              Advanced AI analyzes your financial data and delivers personalized insights, 
              recommendations, and summaries to help you make better money decisions.
            </p>
            <ul className={styles.highlightList}>
              <li><CheckCircle2 size={18} /> Weekly AI spending summaries</li>
              <li><CheckCircle2 size={18} /> Personalized saving tips</li>
              <li><CheckCircle2 size={18} /> Anomaly detection alerts</li>
              <li><CheckCircle2 size={18} /> Goal recommendations</li>
            </ul>
          </div>
          <div className={styles.highlightVisual}>
            <div className={styles.aiInsightCard}>
              <div className={styles.aiHeader}>
                <Sparkles size={20} />
                <span>AI Insight</span>
              </div>
              <p className={styles.aiMessage}>
                "Based on your spending patterns, you could save an extra <strong>$340/month</strong> by 
                reducing dining out from 12 times to 6 times per month. This would add 
                <strong> $4,080</strong> to your savings annually! 🎯"
              </p>
              <div className={styles.aiActions}>
                <button className={styles.aiActionBtn}>Set Budget Alert</button>
                <button className={styles.aiActionSecondary}>Dismiss</button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Security Section */}
      <section id="security" className={styles.securitySection}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionBadge}>SECURITY</span>
          <h2 className={styles.sectionTitle}>Bank-Level Security</h2>
          <p className={styles.sectionSubtitle}>
            Your financial data is protected with enterprise-grade security measures
          </p>
        </div>
        
        <div className={styles.securityGrid}>
          <div className={styles.securityCard}>
            <div className={styles.securityIconLarge}><Lock size={36} /></div>
            <h3>256-bit Encryption</h3>
            <p>All data is encrypted in transit and at rest using military-grade AES-256 encryption.</p>
          </div>
          
          <div className={styles.securityCard}>
            <div className={styles.securityIconLarge}><Shield size={36} /></div>
            <h3>Plaid Protected</h3>
            <p>Bank connections powered by Plaid — we never see or store your bank login credentials.</p>
          </div>
          
          <div className={styles.securityCard}>
            <div className={styles.securityIconLarge}><Zap size={36} /></div>
            <h3>Firebase Auth</h3>
            <p>Enterprise authentication from Google Firebase with multi-factor support.</p>
          </div>
          
          <div className={styles.securityCard}>
            <div className={styles.securityIconLarge}><Eye size={36} /></div>
            <h3>Read-Only Access</h3>
            <p>We can only view your transaction data. No one can move money from your accounts.</p>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className={styles.finalCTA}>
        <div className={styles.ctaGlow}></div>
        <div className={styles.ctaContent}>
          <h2>Ready to Transform Your Finances?</h2>
          <p>Join thousands of users who are building better financial habits with ZEBED</p>
          <button className={styles.ctaPrimaryBtn} onClick={handleGetStarted}>
            Get Started
            <ArrowRight size={22} />
          </button>
          <div className={styles.ctaFeatures}>
            <span><CheckCircle2 size={16} /> Free forever</span>
            <span><CheckCircle2 size={16} /> No credit card</span>
            <span><CheckCircle2 size={16} /> 2 minute setup</span>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className={styles.footer}>
        <div className={styles.footerContent}>
          <div className={styles.footerBrand}>
            <span className={styles.footerLogoIcon}>Z</span>
            <span className={styles.footerLogoText}>ZEBED</span>
          </div>
          <p className={styles.footerTagline}>
            Your money, simplified. Built with 💜 for people who want to win with money.
          </p>
          <div className={styles.footerLinks}>
            <button onClick={() => scrollToSection('features')}>Features</button>
            <button onClick={() => scrollToSection('security')}>Security</button>
            <button onClick={handleGetStarted}>Get Started</button>
          </div>
          <p className={styles.footerCopyright}>
            © {new Date().getFullYear()} ZEBED. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
